import firebase from "firebase/compat/app";
import app from "../firebase";
import { updateAnimalFields, updateAnimalBreedingGroup } from "../api/firestore/services/animalService";
import {
	GENERAL_EVENT_DIAGNOSE_VALUES,
	normalizeGeneralEventDiagnose,
} from "../constants/erpEnums";
import { computeShallowDiff, writeERPAuditEntry } from "./erpAudit";

const db = firebase.firestore(app);
const { FieldValue } = firebase.firestore;

/**
 * Creates a new birth record for an animal
 * @param {string} userID - The unique identifier for the user
 * @param {Object} birthData - Birth data to save
 * @returns {Promise<void>}
 * @path Users/{userID}/Births/{timestamp}
 */
export async function setNewBirth(userID, birthData) {
	// Ensure timestamp exists, use current time if not provided
	const timestamp = birthData.t || Date.now();

	// Validate the birth data has required fields
	if (!birthData.MAC) {
		throw new Error("Animal MAC address is required for birth record");
	}

	if (!birthData.ID) {
		throw new Error("Animal ID is required for birth record");
	}

	// Set default values for optional fields
	const birth = {
		Archived: false,
		Comment: birthData.Comment || "",
		Dead: birthData.Dead || 0,
		ID: birthData.ID,
		Living: birthData.Living || 0,
		MAC: birthData.MAC,
		StartDate: birthData.StartDate || new Date().toISOString().split("T")[0],
		StartTime: birthData.StartTime || new Date().toTimeString().split(" ")[0],
		Uid: birthData.Uid || birthData.MAC, // Fallback to MAC if Uid not provided
		t: timestamp,
		CreatedAt: FieldValue.serverTimestamp(),
		CreatedBy: userID,
		UpdatedAt: FieldValue.serverTimestamp(),
		UpdatedBy: userID,
	};

	// Add new optional fields only if they are provided (backward compatible)
	if (birthData.Mummified !== undefined && birthData.Mummified !== null) {
		birth.Mummified = typeof birthData.Mummified === 'number' ? birthData.Mummified : parseInt(birthData.Mummified) || 0;
	}
	if (birthData.Weaned !== undefined && birthData.Weaned !== null) {
		birth.Weaned = typeof birthData.Weaned === 'number' ? birthData.Weaned : parseInt(birthData.Weaned) || 0;
	}
	if (birthData.Parity !== undefined && birthData.Parity !== null) {
		birth.Parity = typeof birthData.Parity === 'number' ? birthData.Parity : parseInt(birthData.Parity) || 0;
	}
	if (birthData.Assisted !== undefined && birthData.Assisted !== null) {
		birth.Assisted = birthData.Assisted === true || birthData.Assisted === 'true';
	}
	if (birthData.FarrowingDuration !== undefined && birthData.FarrowingDuration !== null) {
		birth.FarrowingDuration = typeof birthData.FarrowingDuration === 'number' ? birthData.FarrowingDuration : parseInt(birthData.FarrowingDuration) || 0;
	}
	if (birthData.Complications !== undefined && birthData.Complications !== null && birthData.Complications !== '') {
		birth.Complications = birthData.Complications;
	}
	if (birthData.AverageLitterWeight !== undefined && birthData.AverageLitterWeight !== null) {
		birth.AverageLitterWeight = typeof birthData.AverageLitterWeight === 'number' ? birthData.AverageLitterWeight : parseFloat(birthData.AverageLitterWeight) || 0;
	}

	// Add the birth record to Firestore
	await db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.doc(timestamp.toString())
		.set(birth)
		.catch((error) => {
			console.error("Error adding birth record:", error);
			throw error;
		});

	try {
		const afterSnapshot = { ...birth };
		delete afterSnapshot.CreatedAt;
		delete afterSnapshot.UpdatedAt;
		await writeERPAuditEntry(userID, {
			action: "create",
			collection: "Births",
			docId: timestamp.toString(),
			actorUid: userID,
			mac: birthData.MAC,
			animalId: birthData.ID,
			after: afterSnapshot,
			diff: computeShallowDiff(null, afterSnapshot),
			source: "ui",
		});
	} catch (e) {
		console.warn("[animalEvents][setNewBirth] - Failed to write audit entry:", e);
	}

	// Create a work history entry for this action
	await createWorkHistoryEntry(userID, {
		CustomName: birth.ID,
		WorkItemType: "Birth",
		t: timestamp,
		SowID: birth.ID,
		Group: birthData.Group || "",
	});

	// Update the animal's Parity if provided
	if (birth.Parity !== undefined && birth.Parity !== null) {
		await updateAnimalFields(userID, birth.MAC, {
			Parity: birth.Parity
		}).catch(err => console.error("Error updating animal parity:", err));
	}
}

/**
 * Creates multiple birth records in a single batch operation
 * This is much more efficient than calling setNewBirth() for each animal
 * @param {string} userID - The unique identifier for the user
 * @param {Array<Object>} birthRecords - Array of birth data objects
 * @param {Function} [onProgress] - Optional callback for progress updates (current, total)
 * @returns {Promise<{success: number, failed: number, errors: Array}>} Results summary
 * @path Users/{userID}/Births/{timestamp}
 */
export async function setBirthsBatch(userID, birthRecords, onProgress) {
	if (!birthRecords || birthRecords.length === 0) {
		return { success: 0, failed: 0, errors: [] };
	}

	const results = { success: 0, failed: 0, errors: [] };
	const batch = db.batch();
	const workHistoryBatch = db.batch();
	const animalUpdates = []; // Track animal Parity updates
	const createdBirths = [];

	// Process each birth record
	for (const birthData of birthRecords) {
		try {
			// Validate required fields
			if (!birthData.MAC) {
				results.failed++;
				results.errors.push({
					animalId: birthData.ID || "unknown",
					error: "Animal MAC address is required",
				});
				continue;
			}

			if (!birthData.ID) {
				results.failed++;
				results.errors.push({
					animalId: birthData.MAC,
					error: "Animal ID is required",
				});
				continue;
			}

			// Generate unique timestamp for each record
			const timestamp = birthData.t || Date.now() + results.success;

			// Build birth record
			const birth = {
				Archived: false,
				Comment: birthData.Comment || "",
				Dead: birthData.Dead || 0,
				ID: birthData.ID,
				Living: birthData.Living || 0,
				MAC: birthData.MAC,
				StartDate: birthData.StartDate || new Date().toISOString().split("T")[0],
				StartTime: birthData.StartTime || new Date().toTimeString().split(" ")[0],
				Uid: birthData.Uid || birthData.MAC,
				t: timestamp,
				CreatedAt: FieldValue.serverTimestamp(),
				CreatedBy: userID,
				UpdatedAt: FieldValue.serverTimestamp(),
				UpdatedBy: userID,
			};

			// Add optional fields if provided
			if (birthData.Mummified !== undefined && birthData.Mummified !== null) {
				birth.Mummified = typeof birthData.Mummified === "number"
					? birthData.Mummified
					: parseInt(birthData.Mummified) || 0;
			}

			if (birthData.Weaned !== undefined && birthData.Weaned !== null) {
				birth.Weaned = typeof birthData.Weaned === "number"
					? birthData.Weaned
					: parseInt(birthData.Weaned) || 0;
			}

			if (birthData.Parity !== undefined && birthData.Parity !== null) {
				birth.Parity = typeof birthData.Parity === "number"
					? birthData.Parity
					: parseInt(birthData.Parity) || 0;
			}

			if (birthData.Assisted !== undefined && birthData.Assisted !== null) {
				birth.Assisted = birthData.Assisted === true || birthData.Assisted === "true";
			}

			if (birthData.FarrowingDuration !== undefined && birthData.FarrowingDuration !== null) {
				birth.FarrowingDuration = typeof birthData.FarrowingDuration === "number"
					? birthData.FarrowingDuration
					: parseInt(birthData.FarrowingDuration) || 0;
			}

			if (birthData.Complications !== undefined && birthData.Complications !== null && birthData.Complications !== "") {
				birth.Complications = birthData.Complications;
			}

			if (birthData.AverageLitterWeight !== undefined && birthData.AverageLitterWeight !== null) {
				birth.AverageLitterWeight = typeof birthData.AverageLitterWeight === "number"
					? birthData.AverageLitterWeight
					: parseFloat(birthData.AverageLitterWeight) || 0;
			}

			// Add birth to batch
			const birthRef = db
				.collection("Users")
				.doc(userID)
				.collection("Births")
				.doc(timestamp.toString());
			batch.set(birthRef, birth);
			createdBirths.push({ timestamp, data: birth, mac: birthData.MAC, animalId: birthData.ID });

			// Add work history entry to batch
			const workHistoryRef = db
				.collection("Users")
				.doc(userID)
				.collection("WorkHistory")
				.doc(timestamp.toString());
			workHistoryBatch.set(workHistoryRef, {
				CustomName: birth.ID,
				WorkItemType: "Birth",
				t: timestamp,
				SowID: birth.ID,
				Group: birthData.Group || "",
			});

			// Track Parity update if provided
			if (birth.Parity !== undefined && birth.Parity !== null) {
				animalUpdates.push({
					mac: birthData.MAC,
					parity: birth.Parity,
				});
			}

			results.success++;

			// Report progress
			if (onProgress) {
				onProgress(results.success + results.failed, birthRecords.length);
			}
		} catch (error) {
			results.failed++;
			results.errors.push({
				animalId: birthData.ID || birthData.MAC || "unknown",
				error: error.message || "Unknown error",
			});
		}
	}

	// Commit birth batch
	if (results.success > 0) {
		try {
			await batch.commit();

			// Audit entries (best-effort, post-commit)
			await Promise.all(
				createdBirths.map(async ({ timestamp, data, mac, animalId }) => {
					try {
						const afterSnapshot = { ...data };
						delete afterSnapshot.CreatedAt;
						delete afterSnapshot.UpdatedAt;
						await writeERPAuditEntry(userID, {
							action: "create",
							collection: "Births",
							docId: String(timestamp),
							actorUid: userID,
							mac,
							animalId,
							after: afterSnapshot,
							diff: computeShallowDiff(null, afterSnapshot),
							source: "ui",
						});
					} catch (e) {
						// ignore
					}
				})
			);
		} catch (error) {
			console.error("[animalEvents][setBirthsBatch] - Error committing batch:", error);
			throw error;
		}

		// Commit work history batch (non-blocking, don't fail if this errors)
		try {
			await workHistoryBatch.commit();
		} catch (error) {
			console.error("[animalEvents][setBirthsBatch] - Error committing work history:", error);
		}

		// Update animal Parity values (done separately as it uses a different update mechanism)
		for (const update of animalUpdates) {
			try {
				if (update.parity !== undefined && update.parity !== null) {
					await updateAnimalFields(userID, update.mac, { Parity: update.parity });
				}
			} catch (error) {
				console.error(`[animalEvents][setBirthsBatch] - Error updating animal ${update.mac}:`, error);
				// Don't fail the batch for animal update errors
			}
		}
	}

	return results;
}

/**
 * Fetches birth records for a specific animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal
 * @returns {Promise<Array>} - Array of birth records
 * @path Users/{userID}/Births
 */
export async function getBirths(userID, mac) {
	try {
		// Query without Archived filter to handle records where Archived is undefined/null
		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Births")
			.where("MAC", "==", mac)
			.get();

		// Filter client-side to exclude archived records (Archived !== true allows undefined/null/false)
		return snapshot.docs
			.map((doc) => {
				const data = doc.data();
				// Ensure the timestamp is included
				return { ...data, t: data.t || parseInt(doc.id) };
			})
			.filter((birth) => birth.Archived !== true);
	} catch (error) {
		console.error("[animalEvents][getBirths] - Error fetching birth records:", error);
		throw error;
	}
}

/**
 * Updates an existing birth record
 * @param {string} userID - The unique identifier for the user
 * @param {Object} birthData - Birth data to update
 * @param {boolean} [archive=false] - Whether to archive the record
 * @returns {Promise<void>}
 * @path Users/{userID}/Births/{birthData.t}
 */
export async function updateBirth(userID, birthData, archive = false) {
	if (!birthData.t) {
		throw new Error("Timestamp is required to update birth record");
	}

	const docRef = db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.doc(birthData.t.toString());

	const beforeSnap = await docRef.get();
	const beforeData = beforeSnap.exists ? beforeSnap.data() : null;

	// If archiving, set archival metadata
	if (archive) {
		await docRef.update({
			Archived: true,
			ArchivedAt: FieldValue.serverTimestamp(),
			ArchivedBy: userID,
			UpdatedAt: FieldValue.serverTimestamp(),
			UpdatedBy: userID,
		}).catch((error) => {
			console.error("Error archiving birth record:", error);
			throw error;
		});

		try {
			const beforeSnapshot = beforeData ? { ...beforeData } : null;
			if (beforeSnapshot) {
				delete beforeSnapshot.CreatedAt;
				delete beforeSnapshot.UpdatedAt;
			}
			const afterSnapshot = { ...(beforeSnapshot || {}), Archived: true };
			await writeERPAuditEntry(userID, {
				action: "archive",
				collection: "Births",
				docId: birthData.t.toString(),
				actorUid: userID,
				mac: birthData.MAC || beforeData?.MAC,
				animalId: birthData.ID || beforeData?.ID,
				before: beforeSnapshot,
				after: afterSnapshot,
				diff: computeShallowDiff(beforeSnapshot, afterSnapshot),
				source: "ui",
			});
		} catch (e) {
			console.warn("[animalEvents][updateBirth] - Failed to write audit entry:", e);
		}

		return;
	}

	// Otherwise update the full record
	const updateData = { ...birthData, UpdatedAt: FieldValue.serverTimestamp(), UpdatedBy: userID };
	await docRef.update(updateData).catch((error) => {
		console.error("Error updating birth record:", error);
		throw error;
	});

	// Update the animal's Parity if provided
	if (birthData.Parity !== undefined && birthData.Parity !== null && birthData.MAC) {
		await updateAnimalFields(userID, birthData.MAC, {
			Parity: birthData.Parity
		}).catch(err => console.error("Error updating animal parity:", err));
	}

	try {
		const beforeSnapshot = beforeData ? { ...beforeData } : null;
		if (beforeSnapshot) {
			delete beforeSnapshot.CreatedAt;
			delete beforeSnapshot.UpdatedAt;
		}
		const afterSnapshot = { ...(beforeSnapshot || {}), ...updateData };
		delete afterSnapshot.CreatedAt;
		delete afterSnapshot.UpdatedAt;
		await writeERPAuditEntry(userID, {
			action: "update",
			collection: "Births",
			docId: birthData.t.toString(),
			actorUid: userID,
			mac: birthData.MAC || beforeData?.MAC,
			animalId: birthData.ID || beforeData?.ID,
			before: beforeSnapshot,
			after: afterSnapshot,
			diff: computeShallowDiff(beforeSnapshot, afterSnapshot),
			source: "ui",
		});
	} catch (e) {
		console.warn("[animalEvents][updateBirth] - Failed to write audit entry:", e);
	}
}

/**
 * Creates a new insemination record
 * @param {string} userID - The unique identifier for the user
 * @param {Object} inseminationData - Insemination data to save
 * @returns {Promise<void>}
 * @path Users/{userID}/Inseminations/{timestamp}
 */
export async function setNewInsemination(userID, inseminationData) {
	// Ensure timestamp exists, use current time if not provided
	const timestamp = inseminationData.t || Date.now();

	// Validate the insemination data has required fields
	if (!inseminationData.MAC) {
		throw new Error("Animal MAC address is required for insemination record");
	}

	if (!inseminationData.ID) {
		throw new Error("Animal ID is required for insemination record");
	}

	// Set default values for optional fields
	const insemination = {
		Archived: false,
		GeneticLine: inseminationData.GeneticLine || "",
		MAC: inseminationData.MAC,
		StartDate:
			inseminationData.StartDate || new Date().toISOString().split("T")[0],
		StartTime:
			inseminationData.StartTime || new Date().toTimeString().split(" ")[0],
		Boar: inseminationData.Boar || "",
		ID: inseminationData.ID,
		Occupied: inseminationData.Occupied || false,
		Tubes: inseminationData.Tubes || 0,
		Uid: inseminationData.Uid || inseminationData.MAC, // Fallback to MAC if Uid not provided
		t: timestamp,
		CreatedAt: FieldValue.serverTimestamp(),
		CreatedBy: userID,
		UpdatedAt: FieldValue.serverTimestamp(),
		UpdatedBy: userID,
	};

	// Add new enhanced fields if provided
	if (inseminationData.serviceOrder !== undefined && inseminationData.serviceOrder !== null) {
		insemination.serviceOrder = typeof inseminationData.serviceOrder === "number"
			? inseminationData.serviceOrder
			: parseInt(inseminationData.serviceOrder) || 1;
	}

	if (inseminationData.serviceType !== undefined && inseminationData.serviceType !== null) {
		// Validate service type
		if (["AI", "natural"].includes(inseminationData.serviceType)) {
			insemination.serviceType = inseminationData.serviceType;
		} else {
			// Default to AI if invalid value provided
			insemination.serviceType = "AI";
		}
	} else {
		// Default to AI if not provided
		insemination.serviceType = "AI";
	}

	if (inseminationData.semenSource !== undefined && inseminationData.semenSource !== null && inseminationData.semenSource !== "") {
		insemination.semenSource = inseminationData.semenSource;
	}

	// Add ScanningResult if it exists
	if (inseminationData.ScanningResult) {
		insemination.ScanningResult = inseminationData.ScanningResult;
	}

	// Add new optional fields only if they are provided (backward compatible)
	if (inseminationData.HeatDetectionDate !== undefined && inseminationData.HeatDetectionDate !== null && inseminationData.HeatDetectionDate !== "") {
		insemination.HeatDetectionDate = typeof inseminationData.HeatDetectionDate === 'string' 
			? inseminationData.HeatDetectionDate 
			: inseminationData.HeatDetectionDate.toISOString().split("T")[0];
	}
	if (inseminationData.ServiceNumber !== undefined && inseminationData.ServiceNumber !== null) {
		insemination.ServiceNumber = typeof inseminationData.ServiceNumber === 'number' ? inseminationData.ServiceNumber : parseInt(inseminationData.ServiceNumber) || 0;
	}
	if (inseminationData.Parity !== undefined && inseminationData.Parity !== null) {
		insemination.Parity = typeof inseminationData.Parity === 'number' ? inseminationData.Parity : parseInt(inseminationData.Parity) || 0;
	}
	if (inseminationData.Technician !== undefined && inseminationData.Technician !== null && inseminationData.Technician !== "") {
		insemination.Technician = inseminationData.Technician;
	}
	if (inseminationData.SemenBatch !== undefined && inseminationData.SemenBatch !== null && inseminationData.SemenBatch !== "") {
		insemination.SemenBatch = inseminationData.SemenBatch;
	}
	if (inseminationData.ExpectedFarrowingDate !== undefined && inseminationData.ExpectedFarrowingDate !== null && inseminationData.ExpectedFarrowingDate !== "") {
		insemination.ExpectedFarrowingDate = typeof inseminationData.ExpectedFarrowingDate === 'string'
			? inseminationData.ExpectedFarrowingDate
			: inseminationData.ExpectedFarrowingDate.toISOString().split("T")[0];
	}
	if (inseminationData.PregnancyCheckDate !== undefined && inseminationData.PregnancyCheckDate !== null && inseminationData.PregnancyCheckDate !== "") {
		insemination.PregnancyCheckDate = typeof inseminationData.PregnancyCheckDate === 'string'
			? inseminationData.PregnancyCheckDate
			: inseminationData.PregnancyCheckDate.toISOString().split("T")[0];
	}
	if (inseminationData.ReturnToEstrusDate !== undefined && inseminationData.ReturnToEstrusDate !== null && inseminationData.ReturnToEstrusDate !== "") {
		insemination.ReturnToEstrusDate = typeof inseminationData.ReturnToEstrusDate === 'string'
			? inseminationData.ReturnToEstrusDate
			: inseminationData.ReturnToEstrusDate.toISOString().split("T")[0];
	}
	// Handle BreedingGroup: Set on animal, NOT on insemination record
	// BreedingGroup is now stored on Animal documents for consistency
	let breedingGroupToSet = null;
	if (inseminationData.BreedingGroup !== undefined && inseminationData.BreedingGroup !== null && inseminationData.BreedingGroup !== "") {
		breedingGroupToSet = inseminationData.BreedingGroup.trim();
		// Note: We will set this on the animal below, not on the insemination record
	}

	// Add the insemination record to Firestore (without BreedingGroup)
	await db
		.collection("Users")
		.doc(userID)
		.collection("Inseminations")
		.doc(timestamp.toString())
		.set(insemination)
		.catch((error) => {
			console.error("Error adding insemination record:", error);
			throw error;
		});

	try {
		const afterSnapshot = { ...insemination };
		delete afterSnapshot.CreatedAt;
		delete afterSnapshot.UpdatedAt;
		await writeERPAuditEntry(userID, {
			action: "create",
			collection: "Inseminations",
			docId: timestamp.toString(),
			actorUid: userID,
			mac: inseminationData.MAC,
			animalId: inseminationData.ID,
			after: afterSnapshot,
			diff: computeShallowDiff(null, afterSnapshot),
			source: "ui",
		});
	} catch (e) {
		console.warn("[animalEvents][setNewInsemination] - Failed to write audit entry:", e);
	}

	// Update animal's BreedingGroup if provided
	// For new inseminations, we allow updating the BreedingGroup even if the animal already has one,
	// because a new insemination represents a new breeding cycle and should update the animal's current BreedingGroup.
	if (breedingGroupToSet) {
		try {
			const updateResult = await updateAnimalBreedingGroup(
				userID,
				insemination.MAC,
				breedingGroupToSet,
				true // Allow override - new insemination represents a new breeding cycle
			);
			
			if (!updateResult.updated) {
				console.warn(`[animalEvents][setNewInsemination] - Could not set BreedingGroup for animal ${insemination.MAC}: ${updateResult.reason}`);
			}
		} catch (error) {
			console.error(`[animalEvents][setNewInsemination] - Error updating animal BreedingGroup for ${insemination.MAC}:`, error);
			// Don't fail insemination creation if BreedingGroup update fails
		}
	}

	// Create a work history entry for this action
	await createWorkHistoryEntry(userID, {
		CustomName: insemination.ID,
		WorkItemType: "Insemination",
		t: timestamp,
		SowID: insemination.ID,
		Group: inseminationData.Group || "",
	});

	// Update the animal's Parity if provided
	if (insemination.Parity !== undefined && insemination.Parity !== null) {
		await updateAnimalFields(userID, insemination.MAC, {
			Parity: insemination.Parity
		}).catch(err => console.error("Error updating animal parity:", err));
	}
}

/**
 * Creates multiple insemination records in a single batch operation
 * This is much more efficient than calling setNewInsemination() for each animal
 * @param {string} userID - The unique identifier for the user
 * @param {Array<Object>} inseminationRecords - Array of insemination data objects
 * @returns {Promise<{success: number, failed: number, errors: Array}>} Results summary
 * @path Users/{userID}/Inseminations/{timestamp}
 */
export async function setInseminationsBatch(userID, inseminationRecords) {
	if (!inseminationRecords || inseminationRecords.length === 0) {
		return { success: 0, failed: 0, errors: [] };
	}

	const results = { success: 0, failed: 0, errors: [] };
	const batch = db.batch();
	const workHistoryBatch = db.batch();
	const animalUpdates = []; // Track animal BreedingGroup updates
	const createdInseminations = [];

	// Process each insemination record
	for (const inseminationData of inseminationRecords) {
		try {
			// Validate required fields
			if (!inseminationData.MAC) {
				results.failed++;
				results.errors.push({
					animalId: inseminationData.ID || "unknown",
					error: "Animal MAC address is required",
				});
				continue;
			}

			if (!inseminationData.ID) {
				results.failed++;
				results.errors.push({
					animalId: inseminationData.MAC,
					error: "Animal ID is required",
				});
				continue;
			}

			// Generate unique timestamp for each record
			const timestamp = inseminationData.t || Date.now() + results.success;

			// Build insemination record
			const insemination = {
				Archived: false,
				GeneticLine: inseminationData.GeneticLine || "",
				MAC: inseminationData.MAC,
				StartDate: inseminationData.StartDate || new Date().toISOString().split("T")[0],
				StartTime: inseminationData.StartTime || new Date().toTimeString().split(" ")[0],
				Boar: inseminationData.Boar || "",
				ID: inseminationData.ID,
				Occupied: inseminationData.Occupied || false,
				Tubes: inseminationData.Tubes || 0,
				Uid: inseminationData.Uid || inseminationData.MAC,
				t: timestamp,
				CreatedAt: FieldValue.serverTimestamp(),
				CreatedBy: userID,
				UpdatedAt: FieldValue.serverTimestamp(),
				UpdatedBy: userID,
			};

			// Add optional fields
			if (inseminationData.serviceOrder !== undefined && inseminationData.serviceOrder !== null) {
				insemination.serviceOrder = typeof inseminationData.serviceOrder === "number"
					? inseminationData.serviceOrder
					: parseInt(inseminationData.serviceOrder) || 1;
			}

			if (inseminationData.serviceType !== undefined && inseminationData.serviceType !== null) {
				insemination.serviceType = ["AI", "natural"].includes(inseminationData.serviceType)
					? inseminationData.serviceType
					: "AI";
			} else {
				insemination.serviceType = "AI";
			}

			if (inseminationData.semenSource) {
				insemination.semenSource = inseminationData.semenSource;
			}

			if (inseminationData.ScanningResult) {
				insemination.ScanningResult = inseminationData.ScanningResult;
			}

			if (inseminationData.HeatDetectionDate) {
				insemination.HeatDetectionDate = typeof inseminationData.HeatDetectionDate === "string"
					? inseminationData.HeatDetectionDate
					: inseminationData.HeatDetectionDate.toISOString().split("T")[0];
			}

			if (inseminationData.ServiceNumber !== undefined && inseminationData.ServiceNumber !== null) {
				insemination.ServiceNumber = typeof inseminationData.ServiceNumber === "number"
					? inseminationData.ServiceNumber
					: parseInt(inseminationData.ServiceNumber) || 0;
			}

			if (inseminationData.Parity !== undefined && inseminationData.Parity !== null) {
				insemination.Parity = typeof inseminationData.Parity === "number"
					? inseminationData.Parity
					: parseInt(inseminationData.Parity) || 0;
			}

			if (inseminationData.Technician) {
				insemination.Technician = inseminationData.Technician;
			}

			if (inseminationData.SemenBatch) {
				insemination.SemenBatch = inseminationData.SemenBatch;
			}

			if (inseminationData.ExpectedFarrowingDate) {
				insemination.ExpectedFarrowingDate = typeof inseminationData.ExpectedFarrowingDate === "string"
					? inseminationData.ExpectedFarrowingDate
					: inseminationData.ExpectedFarrowingDate.toISOString().split("T")[0];
			}

			if (inseminationData.PregnancyCheckDate) {
				insemination.PregnancyCheckDate = typeof inseminationData.PregnancyCheckDate === "string"
					? inseminationData.PregnancyCheckDate
					: inseminationData.PregnancyCheckDate.toISOString().split("T")[0];
			}

			if (inseminationData.ReturnToEstrusDate) {
				insemination.ReturnToEstrusDate = typeof inseminationData.ReturnToEstrusDate === "string"
					? inseminationData.ReturnToEstrusDate
					: inseminationData.ReturnToEstrusDate.toISOString().split("T")[0];
			}

			// Add insemination to batch
			const insemRef = db
				.collection("Users")
				.doc(userID)
				.collection("Inseminations")
				.doc(timestamp.toString());
			batch.set(insemRef, insemination);
			createdInseminations.push({ timestamp, data: insemination, mac: inseminationData.MAC, animalId: inseminationData.ID });

			// Add work history entry to batch
			const workHistoryRef = db
				.collection("Users")
				.doc(userID)
				.collection("WorkHistory")
				.doc(timestamp.toString());
			workHistoryBatch.set(workHistoryRef, {
				CustomName: insemination.ID,
				WorkItemType: "Insemination",
				t: timestamp,
				SowID: insemination.ID,
				Group: inseminationData.Group || "",
			});

			// Track BreedingGroup update if provided
			if (inseminationData.BreedingGroup && inseminationData.BreedingGroup.trim() !== "") {
				animalUpdates.push({
					mac: inseminationData.MAC,
					breedingGroup: inseminationData.BreedingGroup.trim(),
					parity: insemination.Parity,
				});
			} else if (insemination.Parity !== undefined) {
				// Still update parity even without BreedingGroup
				animalUpdates.push({
					mac: inseminationData.MAC,
					breedingGroup: null,
					parity: insemination.Parity,
				});
			}

			results.success++;
		} catch (error) {
			results.failed++;
			results.errors.push({
				animalId: inseminationData.ID || inseminationData.MAC || "unknown",
				error: error.message || "Unknown error",
			});
		}
	}

	// Commit insemination batch
	if (results.success > 0) {
		try {
			await batch.commit();

			// Audit entries (best-effort, post-commit)
			await Promise.all(
				createdInseminations.map(async ({ timestamp, data, mac, animalId }) => {
					try {
						const afterSnapshot = { ...data };
						delete afterSnapshot.CreatedAt;
						delete afterSnapshot.UpdatedAt;
						await writeERPAuditEntry(userID, {
							action: "create",
							collection: "Inseminations",
							docId: String(timestamp),
							actorUid: userID,
							mac,
							animalId,
							after: afterSnapshot,
							diff: computeShallowDiff(null, afterSnapshot),
							source: "ui",
						});
					} catch (e) {
						// ignore
					}
				})
			);
		} catch (error) {
			console.error("[animalEvents][setInseminationsBatch] - Error committing batch:", error);
			throw error;
		}

		// Commit work history batch (non-blocking, don't fail if this errors)
		try {
			await workHistoryBatch.commit();
		} catch (error) {
			console.error("[animalEvents][setInseminationsBatch] - Error committing work history:", error);
		}

		// Update animal BreedingGroups and Parity (done separately as it uses a different update mechanism)
		for (const update of animalUpdates) {
			try {
				if (update.breedingGroup) {
					await updateAnimalBreedingGroup(userID, update.mac, update.breedingGroup, true);
				}
				if (update.parity !== undefined && update.parity !== null) {
					await updateAnimalFields(userID, update.mac, { Parity: update.parity });
				}
			} catch (error) {
				console.error(`[animalEvents][setInseminationsBatch] - Error updating animal ${update.mac}:`, error);
				// Don't fail the batch for animal update errors
			}
		}
	}

	return results;
}

/**
 * Fetches insemination records for a specific animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal
 * @returns {Promise<Array>} - Array of insemination records
 * @path Users/{userID}/Inseminations
 */
export async function getInseminations(userID, mac) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Inseminations")
			.where("MAC", "==", mac)
			.where("Archived", "==", false)
			.get();

		return snapshot.docs.map((doc) => {
			const data = doc.data();
			// Ensure the timestamp is included
			return { ...data, t: data.t || parseInt(doc.id) };
		});
	} catch (error) {
		console.error("Error fetching insemination records:", error);
		throw error;
	}
}

/**
 * Fetches insemination records for multiple animals in a single query
 * More efficient than calling getInseminations() for each animal
 * @param {string} userID - The unique identifier for the user
 * @param {Array<string>} macs - Array of MAC addresses
 * @returns {Promise<Map<string, Array>>} - Map of MAC -> insemination records
 * @path Users/{userID}/Inseminations
 */
export async function getInseminationsForAnimals(userID, macs) {
	if (!macs || macs.length === 0) {
		return new Map();
	}

	try {
		// Firestore 'in' operator has a limit of 30 items, so we batch the queries
		const BATCH_SIZE = 30;
		const results = new Map();

		// Initialize empty arrays for all MACs
		macs.forEach((mac) => results.set(mac, []));

		// Process in batches
		for (let i = 0; i < macs.length; i += BATCH_SIZE) {
			const batch = macs.slice(i, i + BATCH_SIZE);

			const snapshot = await db
				.collection("Users")
				.doc(userID)
				.collection("Inseminations")
				.where("MAC", "in", batch)
				.where("Archived", "==", false)
				.get();

			snapshot.docs.forEach((doc) => {
				const data = doc.data();
				const record = { ...data, t: data.t || parseInt(doc.id) };
				const existing = results.get(record.MAC) || [];
				existing.push(record);
				results.set(record.MAC, existing);
			});
		}

		return results;
	} catch (error) {
		console.error("[animalEvents][getInseminationsForAnimals] - Error:", error);
		throw error;
	}
}

/**
 * Updates an existing insemination record
 * @param {string} userID - The unique identifier for the user
 * @param {Object} inseminationData - Insemination data to update
 * @param {boolean} [archive=false] - Whether to archive the record
 * @returns {Promise<void>}
 * @path Users/{userID}/Inseminations/{inseminationData.t}
 */
export async function updateInsemination(
	userID,
	inseminationData,
	archive = false
) {
	if (!inseminationData.t) {
		throw new Error("Timestamp is required to update insemination record");
	}

	const docRef = db
		.collection("Users")
		.doc(userID)
		.collection("Inseminations")
		.doc(inseminationData.t.toString());

	const beforeSnap = await docRef.get();
	const beforeData = beforeSnap.exists ? beforeSnap.data() : null;

	// If archiving, just set the Archived flag
	if (archive) {
		await docRef.update({
			Archived: true,
			ArchivedAt: FieldValue.serverTimestamp(),
			ArchivedBy: userID,
			UpdatedAt: FieldValue.serverTimestamp(),
			UpdatedBy: userID,
		}).catch((error) => {
			console.error("Error archiving insemination record:", error);
			throw error;
		});

		try {
			const beforeSnapshot = beforeData ? { ...beforeData } : null;
			if (beforeSnapshot) {
				delete beforeSnapshot.CreatedAt;
				delete beforeSnapshot.UpdatedAt;
			}
			const afterSnapshot = { ...(beforeSnapshot || {}), Archived: true };
			await writeERPAuditEntry(userID, {
				action: "archive",
				collection: "Inseminations",
				docId: inseminationData.t.toString(),
				actorUid: userID,
				mac: inseminationData.MAC || beforeData?.MAC,
				animalId: inseminationData.ID || beforeData?.ID,
				before: beforeSnapshot,
				after: afterSnapshot,
				diff: computeShallowDiff(beforeSnapshot, afterSnapshot),
				source: "ui",
			});
		} catch (e) {
			console.warn("[animalEvents][updateInsemination] - Failed to write audit entry:", e);
		}
		return;
	}

	// Handle BreedingGroup: Set on animal, NOT on insemination record
	// BreedingGroup is now stored on Animal documents for consistency
	// Check if BreedingGroup is explicitly provided (including empty/null to clear it)
	const breedingGroupProvided = inseminationData.BreedingGroup !== undefined;
	const breedingGroupToSet = breedingGroupProvided && inseminationData.BreedingGroup !== null && inseminationData.BreedingGroup !== ""
		? inseminationData.BreedingGroup.trim()
		: null;
	const shouldClearBreedingGroup = breedingGroupProvided && (inseminationData.BreedingGroup === null || inseminationData.BreedingGroup === "");

	// Remove BreedingGroup from insemination data before saving
	const updateData = { ...inseminationData };
	delete updateData.BreedingGroup;
	updateData.UpdatedAt = FieldValue.serverTimestamp();
	updateData.UpdatedBy = userID;

	// Update the insemination record (without BreedingGroup)
	await docRef.update(updateData).catch((error) => {
		console.error("Error updating insemination record:", error);
		throw error;
	});

	try {
		const beforeSnapshot = beforeData ? { ...beforeData } : null;
		if (beforeSnapshot) {
			delete beforeSnapshot.CreatedAt;
			delete beforeSnapshot.UpdatedAt;
		}
		const afterSnapshot = { ...(beforeSnapshot || {}), ...updateData };
		delete afterSnapshot.CreatedAt;
		delete afterSnapshot.UpdatedAt;
		await writeERPAuditEntry(userID, {
			action: "update",
			collection: "Inseminations",
			docId: inseminationData.t.toString(),
			actorUid: userID,
			mac: inseminationData.MAC || beforeData?.MAC,
			animalId: inseminationData.ID || beforeData?.ID,
			before: beforeSnapshot,
			after: afterSnapshot,
			diff: computeShallowDiff(beforeSnapshot, afterSnapshot),
			source: "ui",
		});
	} catch (e) {
		console.warn("[animalEvents][updateInsemination] - Failed to write audit entry:", e);
	}

	// Update animal's BreedingGroup if provided or explicitly cleared
	// When editing an insemination, allow updating the BreedingGroup since the user is explicitly changing it.
	// If BreedingGroup is explicitly cleared (empty/null), clear it on the animal as well.
	if (inseminationData.MAC) {
		try {
			if (shouldClearBreedingGroup) {
				// Explicitly clear BreedingGroup on animal
				const clearResult = await updateAnimalBreedingGroup(
					userID,
					inseminationData.MAC,
					null,
					true // forceClear - allow clearing existing BreedingGroup
				);
				
				if (!clearResult.updated) {
					console.warn(`[animalEvents][updateInsemination] - Could not clear BreedingGroup for animal ${inseminationData.MAC}: ${clearResult.reason}`);
				}
			} else if (breedingGroupToSet) {
				// Update BreedingGroup on animal
				const updateResult = await updateAnimalBreedingGroup(
					userID,
					inseminationData.MAC,
					breedingGroupToSet,
					true // Allow override - user is explicitly updating the BreedingGroup
				);
				
				if (!updateResult.updated) {
					console.warn(`[animalEvents][updateInsemination] - Could not set BreedingGroup for animal ${inseminationData.MAC}: ${updateResult.reason}`);
				}
			}
		} catch (error) {
			console.error(`[animalEvents][updateInsemination] - Error updating animal BreedingGroup for ${inseminationData.MAC}:`, error);
			// Don't fail insemination update if BreedingGroup update fails
		}
	}
}

/**
 * Creates a work history entry for tracking operations
 * @param {string} userID - The unique identifier for the user
 * @param {Object} entry - The work history entry data
 * @returns {Promise<void>}
 * @path Users/{userID}/WorkHistory/{timestamp}
 * @private
 */
async function createWorkHistoryEntry(userID, entry) {
	const timestamp = entry.t || Date.now();

	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("WorkHistory")
			.doc(timestamp.toString())
			.set(entry);
	} catch (error) {
		console.error("Error creating work history entry:", error);
		// Don't throw here to prevent failing the main operation
	}
}

/**
 * Get all animal events history combined
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal
 * @returns {Promise<Array>} Combined array of births and inseminations
 */
export async function getAnimalEventsHistory(userID, mac) {
	try {
		// Fetch all event types for this animal
		const [births, inseminations] = await Promise.all([
			getBirths(userID, mac),
			getInseminations(userID, mac),
		]);

		// Add type identifier to each record
		const typedBirths = births.map((b) => ({ ...b, type: "birth" }));
		const typedInseminations = inseminations.map((i) => ({
			...i,
			type: "insemination",
		}));

		// Combine all events
		const allEvents = [...typedBirths, ...typedInseminations];

		// Sort by timestamp (newest first)
		allEvents.sort((a, b) => b.t - a.t);

		return allEvents;
	} catch (error) {
		console.error("Error fetching animal events history:", error);
		throw error;
	}
}

/**
 * Fetches all insemination records for a user across all animals
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of insemination records
 * @path Users/{userID}/Inseminations
 */
export async function getAllInseminations(userID) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Inseminations")
			.where("Archived", "==", false)
			.get();

		return snapshot.docs.map((doc) => {
			const data = doc.data();
			// Ensure the timestamp is included
			return { ...data, t: data.t || parseInt(doc.id) };
		});
	} catch (error) {
		console.error("Error fetching all insemination records:", error);
		throw error;
	}
}

/**
 * Creates a new general event record
 * @param {string} userID - The unique identifier for the user
 * @param {Object} eventData - General event data to save
 * @returns {Promise<void>}
 * @path Users/{userID}/GeneralEvents/{timestamp}
 */
export async function setNewGeneralEvent(userID, eventData) {
	// Ensure timestamp exists, use current time if not provided
	const timestamp = eventData.t || Date.now();

	// Validate the event data has required fields
	if (!eventData.MAC) {
		throw new Error("Animal MAC address is required for general event record");
	}

	if (!eventData.ID) {
		throw new Error("Animal ID is required for general event record");
	}

	if (!eventData.Diagnose) {
		throw new Error("Diagnose is required for general event record");
	}

	const normalizedDiagnose = normalizeGeneralEventDiagnose(eventData.Diagnose);
	if (!normalizedDiagnose) {
		throw new Error(`Diagnose must be one of: ${GENERAL_EVENT_DIAGNOSE_VALUES.join(", ")}`);
	}

	// Set default values for optional fields
	const event = {
		Archived: false,
		MAC: eventData.MAC,
		Uid: eventData.Uid || eventData.MAC, // Set Uid to MAC as per documentation
		t: timestamp,
		ID: eventData.ID,
		Diagnose: normalizedDiagnose,
		Description: eventData.Description || "",
		Token: eventData.Token || "-", // Token is optional, use '-' as default
		CreatedAt: FieldValue.serverTimestamp(),
		CreatedBy: userID,
		UpdatedAt: FieldValue.serverTimestamp(),
		UpdatedBy: userID,
	};

	// Add the general event record to Firestore
	await db
		.collection("Users")
		.doc(userID)
		.collection("GeneralEvents")
		.doc(timestamp.toString())
		.set(event)
		.catch((error) => {
			console.error("[animalEvents][setNewGeneralEvent] - Error:", error);
			throw error;
		});

	// Create a work history entry for this action
	await createWorkHistoryEntry(userID, {
		CustomName: event.ID,
		WorkItemType: event.Diagnose, // Use Diagnose as WorkItemType (Sickness, Death, Lameness, Other)
		t: timestamp,
		SowID: event.ID,
		Group: eventData.Group || "",
	});

	try {
		const afterSnapshot = { ...event };
		delete afterSnapshot.CreatedAt;
		delete afterSnapshot.UpdatedAt;
		await writeERPAuditEntry(userID, {
			action: "create",
			collection: "GeneralEvents",
			docId: timestamp.toString(),
			actorUid: userID,
			mac: eventData.MAC,
			animalId: eventData.ID,
			after: afterSnapshot,
			diff: computeShallowDiff(null, afterSnapshot),
			source: "ui",
		});
	} catch (e) {
		console.warn("[animalEvents][setNewGeneralEvent] - Failed to write audit entry:", e);
	}
}

/**
 * Updates an existing general event record
 * @param {string} userID - The unique identifier for the user
 * @param {Object} eventData - General event data to update
 * @param {boolean} [archive=false] - Whether to archive the record
 * @returns {Promise<void>}
 * @path Users/{userID}/GeneralEvents/{eventData.t}
 */
export async function updateGeneralEvent(
	userID,
	eventData,
	archive = false
) {
	if (!eventData.t) {
		throw new Error("Timestamp is required to update general event record");
	}

	const docRef = db
		.collection("Users")
		.doc(userID)
		.collection("GeneralEvents")
		.doc(eventData.t.toString());

	const beforeSnap = await docRef.get();
	const beforeData = beforeSnap.exists ? beforeSnap.data() : null;

	// If archiving, just set the Archived flag
	if (archive) {
		await docRef.update({
			Archived: true,
			ArchivedAt: FieldValue.serverTimestamp(),
			ArchivedBy: userID,
			UpdatedAt: FieldValue.serverTimestamp(),
			UpdatedBy: userID,
		}).catch((error) => {
			console.error("[animalEvents][updateGeneralEvent] - Error archiving:", error);
			throw error;
		});

		try {
			const beforeSnapshot = beforeData ? { ...beforeData } : null;
			if (beforeSnapshot) {
				delete beforeSnapshot.CreatedAt;
				delete beforeSnapshot.UpdatedAt;
			}
			const afterSnapshot = { ...(beforeSnapshot || {}), Archived: true };
			await writeERPAuditEntry(userID, {
				action: "archive",
				collection: "GeneralEvents",
				docId: eventData.t.toString(),
				actorUid: userID,
				mac: eventData.MAC || beforeData?.MAC,
				animalId: eventData.ID || beforeData?.ID,
				before: beforeSnapshot,
				after: afterSnapshot,
				diff: computeShallowDiff(beforeSnapshot, afterSnapshot),
				source: "ui",
			});
		} catch (e) {
			console.warn("[animalEvents][updateGeneralEvent] - Failed to write audit entry:", e);
		}
		return;
	}

	const normalizedDiagnose = normalizeGeneralEventDiagnose(eventData.Diagnose);
	if (!normalizedDiagnose) {
		throw new Error(`Diagnose must be one of: ${GENERAL_EVENT_DIAGNOSE_VALUES.join(", ")}`);
	}

	// Otherwise update the full record
	// Clean the object to only include valid fields
	const updateData = {
		Diagnose: normalizedDiagnose,
		Description: eventData.Description || "",
		MAC: eventData.MAC,
		Uid: eventData.Uid || eventData.MAC,
		ID: eventData.ID,
		UpdatedAt: FieldValue.serverTimestamp(),
		UpdatedBy: userID,
	};

	await docRef.update(updateData).catch((error) => {
		console.error("[animalEvents][updateGeneralEvent] - Error updating:", error);
		throw error;
	});

	try {
		const beforeSnapshot = beforeData ? { ...beforeData } : null;
		if (beforeSnapshot) {
			delete beforeSnapshot.CreatedAt;
			delete beforeSnapshot.UpdatedAt;
		}
		const afterSnapshot = { ...(beforeSnapshot || {}), ...updateData };
		delete afterSnapshot.CreatedAt;
		delete afterSnapshot.UpdatedAt;
		await writeERPAuditEntry(userID, {
			action: "update",
			collection: "GeneralEvents",
			docId: eventData.t.toString(),
			actorUid: userID,
			mac: eventData.MAC || beforeData?.MAC,
			animalId: eventData.ID || beforeData?.ID,
			before: beforeSnapshot,
			after: afterSnapshot,
			diff: computeShallowDiff(beforeSnapshot, afterSnapshot),
			source: "ui",
		});
	} catch (e) {
		console.warn("[animalEvents][updateGeneralEvent] - Failed to write audit entry:", e);
	}
}

/**
 * Marks an animal as healthy by creating a HealthyMarker general event
 * and updating the animal doc with lastHealthyMarker timestamp and healthyMarkerCount.
 * Used for lameness baseline seeding — the ML pipeline treats the 24h sensor window
 * around each marker as a baseline observation.
 * @param {string} userID - The unique identifier for the user
 * @param {Object} animalData - Animal data ({ MAC, ID, Uid, Group })
 * @param {string} [description] - Optional description
 * @returns {Promise<void>}
 */
export async function markAnimalHealthy(userID, animalData, description) {
	if (!animalData.MAC) {
		throw new Error("Animal MAC address is required");
	}
	if (!animalData.ID) {
		throw new Error("Animal ID is required");
	}

	const timestamp = Date.now();

	// 1. Create a HealthyMarker general event
	await setNewGeneralEvent(userID, {
		MAC: animalData.MAC,
		Uid: animalData.Uid || animalData.MAC,
		ID: animalData.ID,
		Diagnose: "HealthyMarker",
		Description: description || "",
		Group: animalData.Group || "",
		t: timestamp,
		source: "manual",
	});

	// 2. Update the Animal doc with lastHealthyMarker and increment healthyMarkerCount
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(animalData.MAC)
			.update({
				lastHealthyMarker: timestamp,
				healthyMarkerCount: FieldValue.increment(1),
			});
	} catch (error) {
		console.error("[animalEvents][markAnimalHealthy] - Error updating animal doc:", error);
		// Don't throw — the event was already created successfully
	}
}
